import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Ticket, Minus, Plus, Save, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LottoVerlaufGrouped from '../../components/LottoVerlaufGrouped';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let CURRENT_ROLE = '';
try { CURRENT_ROLE = (JSON.parse(localStorage.getItem('user') || '{}').role) || ''; } catch { CURRENT_ROLE = ''; }
const CAN_SEE_HISTORY = ['admin', 'superadmin', 'stationsleitung'].includes(CURRENT_ROLE);

export const LottoEmployeeRubellose = () => {
  const [products, setProducts] = useState([]);
  const [newStocks, setNewStocks] = useState({});
  const [originalStocks, setOriginalStocks] = useState({}); // Original Admin-Bestand
  const [loadedPositions, setLoadedPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  // iOS-PWA feuert nach einem echten Touch ~300ms später einen synthetischen
  // "click" (Ghost-Click) → derselbe Handler lief 2× pro Tap (Bestand sprang um 2).
  // Guard: identische Aktion auf dasselbe Produkt innerhalb von 400ms wird ignoriert.
  const lastTapRef = useRef({});
  const dedupeTap = (key) => {
    const now = Date.now();
    if (now - (lastTapRef.current[key] || 0) < 400) return false;
    lastTapRef.current[key] = now;
    return true;
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const getUpInitPos = (stock, stockInitial) => {
    const maxS = stockInitial || stock;
    if (stock <= 0) return maxS + 1;   // ausverkauft: alle verkauft → Position hinter der letzten Karte
    if (stock >= maxS) return 1;        // frisch: nichts verkauft → Karte 1
    return maxS - stock + 1;            // aktuelle Losnummer = verkauft + 1
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/lotto/products/reporting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const prods = response.data || [];
      setProducts(prods);
      
      // Initialwert: "down" = aktueller Bestand; "up" = aus gespeichertem Bestand abgeleitet
      const initialStocks = {};
      const origStocks = {};
      const loadedPos = {};
      prods.forEach(p => {
        const maxStock = p.stock_initial || p.stock;
        if (p.count_direction === 'up') {
          const pos = getUpInitPos(p.stock, p.stock_initial || p.stock);
          initialStocks[p.id] = pos;
          loadedPos[p.id] = pos;
        } else {
          initialStocks[p.id] = p.stock;
        }
        origStocks[p.id] = maxStock;
      });
      setNewStocks(initialStocks);
      setOriginalStocks(origStocks);
      setLoadedPositions(loadedPos);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const getDirection = (productId) => {
    const p = products.find(x => x.id === productId);
    return p?.count_direction || 'down';
  };

  const handleIncrement = (productId) => {
    if (!dedupeTap(`${productId}:inc`)) return;
    const dir = getDirection(productId);
    const maxStock = originalStocks[productId] || 0;
    const maxPos = dir === 'up' ? maxStock + 1 : maxStock;  // up: +1 erlaubt Ausverkauft-Zustand (letzte Karte verkauft)
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.min(maxPos, (prev[productId] || (dir === 'up' ? 1 : 0)) + 1),
    }));
  };

  const handleDecrement = (productId) => {
    if (!dedupeTap(`${productId}:dec`)) return;
    const dir = getDirection(productId);
    const minPos = dir === 'up' ? (loadedPositions[productId] ?? 1) : 0;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.max(minPos, (prev[productId] || 0) - 1),
    }));
  };

  const handleIncrement5 = (productId) => {
    if (!dedupeTap(`${productId}:inc5`)) return;
    const dir = getDirection(productId);
    const maxStock = originalStocks[productId] || 0;
    const maxPos = dir === 'up' ? maxStock + 1 : maxStock;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.min(maxPos, (prev[productId] || (dir === 'up' ? 1 : 0)) + 5),
    }));
  };

  const handleDecrement5 = (productId) => {
    if (!dedupeTap(`${productId}:dec5`)) return;
    const dir = getDirection(productId);
    const minPos = dir === 'up' ? (loadedPositions[productId] ?? 1) : 0;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.max(minPos, (prev[productId] || 0) - 5),
    }));
  };

  const getDifference = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;
    if (product.count_direction === 'up') {
      const pos = newStocks[productId] || 1;
      const loaded = loadedPositions[productId] ?? 1;
      return Math.max(0, pos - loaded); // neu verkauft seit letztem Laden (verkauft = pos - 1)
    }
    return (newStocks[productId] || 0) - product.stock;
  };

  const hasChanges = () => {
    return Object.entries(newStocks).some(([productId, pos]) => {
      const product = products.find(p => p.id === productId);
      if (!product) return false;
      if (product.count_direction === 'up') {
        const loaded = loadedPositions[productId] ?? 1;
        return pos > loaded;
      }
      return pos !== product.stock;
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const batchId = (window.crypto && window.crypto.randomUUID)
        ? `BATCH-${window.crypto.randomUUID()}`
        : `BATCH-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      // ALLE freigegebenen Lose in EINER Meldung speichern (auch unverändert / 0 verkauft)
      const items = products.map((product) => {
        const productId = product.id;
        if (product.count_direction === 'up') {
          const maxStock = originalStocks[productId] || 0;
          const position = newStocks[productId] ?? (loadedPositions[productId] ?? 1);
          const remaining = Math.max(0, maxStock - (position - 1));
          return { product_id: productId, new_stock: remaining };
        }
        const remaining = newStocks[productId] ?? (product.stock || 0);
        return { product_id: productId, new_stock: remaining };
      });

      await axios.post(`${API}/lotto/report-batch`, { batch_id: batchId, items }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Bestand bestätigt – Meldung gespeichert');
      fetchProducts();
      setHistoryRefresh((n) => n + 1);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getStockStatus = (product) => {
    const pos = newStocks[product.id] ?? (product.count_direction === 'up' ? 1 : (originalStocks[product.id] || 0));
    const threshold = product.warning_threshold;
    if (product.count_direction === 'up') {
      const maxStock = originalStocks[product.id] || 0;
      if (pos > maxStock) return { color: 'bg-gray-500', text: 'Ausverkauft', icon: '⚫' };
      if (pos === maxStock) return { color: 'bg-yellow-500', text: 'Letzte Karte', icon: '🟡' };
      if (pos === 1) return { color: 'bg-gray-400', text: 'Nicht gestartet', icon: '⚪' };
      return { color: 'bg-blue-500', text: 'Zählung läuft', icon: '🔵' };
    }
    const currentStock = pos;
    if (currentStock <= 0) return { color: 'bg-gray-500', text: 'Ausverkauft', icon: '⚫' };
    if (currentStock < threshold) return { color: 'bg-red-500', text: 'Kritisch', icon: '🔴' };
    if (currentStock === threshold) return { color: 'bg-yellow-500', text: 'Niedrig', icon: '🟡' };
    return { color: 'bg-green-500', text: 'Gut', icon: '🟢' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24 md:pb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              🎰 Rubellose
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Aktualisieren Sie den Bestand mit + und -
            </p>
          </div>
        </div>

        {products.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="py-16 text-center">
              <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Keine Produkte zum Melden verfügbar</p>
              <p className="text-muted-foreground mt-2">
                Kontaktieren Sie Ihren Administrator
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 sm:py-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Ticket className="h-4 w-4 sm:h-5 sm:w-5" />
                  Alle Produkte
                  <Badge variant="secondary" className="ml-auto bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs sm:text-sm">
                    {products.length} Produkte
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {products.map(product => {
                    const status = getStockStatus(product);
                    const difference = getDifference(product.id);
                    const isUp = product.count_direction === 'up';
                    // ?? statt || : bei Rückwärts muss der Wert 0 erhalten bleiben (0 || 1 → 1 war der Bug)
                    const pos = newStocks[product.id] ?? (isUp ? 1 : (originalStocks[product.id] || 0));
                    const currentStock = pos;
                    const maxStock = originalStocks[product.id] || 0;
                    // Ausverkauft: vorwärts wenn pos > Bestand, rückwärts wenn pos <= 0 (letztes Los verkauft)
                    const isSoldOut = isUp ? pos > maxStock : pos <= 0;
                    // Letzte Karte (noch 1 übrig): vorwärts pos == Bestand, rückwärts pos == 1
                    const isLastCard = isUp ? pos === maxStock : pos === 1;
                    const isComplete = isSoldOut;
                    
                    return (
                      <div 
                        key={product.id} 
                        className={`p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors ${
                          difference !== 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        {/* Mobile Layout - Kompakt */}
                        <div className="flex items-center gap-2 md:hidden">
                          {/* Status Dot */}
                          <span className="text-base flex-shrink-0">{status.icon}</span>
                          
                          {/* Produktinfo - links */}
                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-sm leading-tight text-slate-900 dark:text-slate-100">{product.name}</span>
                              <span className="text-xs text-gray-500 dark:text-slate-400 flex-shrink-0">{new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(product.price)} €</span>
                              {difference !== 0 && (
                                <span className={`text-xs font-semibold flex-shrink-0 ${
                                  difference < 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {difference > 0 ? '+' : ''}{difference}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Controls - rechts, kompakt */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDecrement(product.id)}
                              disabled={isUp ? pos <= (loadedPositions[product.id] ?? 1) : pos === 0}
                              data-testid={`lotto-minus-${product.id}`}
                              className="h-9 w-9 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border rounded-lg disabled:opacity-40 touch-manipulation select-none"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            
                            <div data-testid={`lotto-value-${product.id}`} className={`h-9 flex items-center justify-center rounded-lg border font-bold text-sm flex-shrink-0 px-2 min-w-[3rem] ${
                              isSoldOut
                                ? 'bg-gray-200 border-gray-400 text-gray-500 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-400'
                                : isLastCard
                                  ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                                  : isUp && pos > 1
                                    ? 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-500 dark:text-blue-300'
                                    : pos > 0 && !isUp
                                      ? pos < product.warning_threshold
                                        ? 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-500 dark:text-red-300'
                                        : 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-500 dark:text-blue-300'
                                      : 'bg-gray-50 border-gray-300 text-gray-400 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-400'
                            }`}>
                              {isSoldOut ? 'Aus' : pos}
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleIncrement(product.id)}
                              disabled={isUp ? isSoldOut : pos >= originalStocks[product.id]}
                              data-testid={`lotto-plus-${product.id}`}
                              className="h-9 w-9 p-0 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border rounded-lg disabled:opacity-40 touch-manipulation select-none"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-2xl">{status.icon}</span>
                            <div className="flex flex-col">
                              <span className="font-medium text-slate-900 dark:text-slate-100">{product.name}</span>
                              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
                                <span>{new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(product.price)} €</span>
                              </div>
                            </div>
                            {difference !== 0 && (
                              <Badge 
                                variant="secondary" 
                                className={`ml-4 ${
                                  difference < 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {difference > 0 ? '+' : ''}{difference} Stück
                              </Badge>
                            )}
                            {currentStock <= product.warning_threshold && currentStock > 0 && !isUp && (
                              <div className="flex items-center gap-1 text-yellow-700 text-sm ml-4">
                                <AlertTriangle className="h-4 w-4" />
                                <span>Warnschwelle ({product.warning_threshold})</span>
                              </div>
                            )}
                            {isComplete && (
                              <div className="flex items-center gap-1.5 text-green-700 dark:text-green-400 text-sm ml-4 font-semibold">
                                <span className="text-base">✓</span>
                                <span>Vollständig gezählt</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDecrement5(product.id)}
                              disabled={currentStock < 5}
                              className="h-10 w-10 p-0 text-gray-500 disabled:opacity-30"
                            >
                              -5
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDecrement(product.id)}
                              disabled={isUp ? pos <= (loadedPositions[product.id] ?? 1) : pos === 0}
                              className="h-12 w-12 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 disabled:opacity-30 touch-manipulation select-none"
                            >
                              <Minus className="h-5 w-5" />
                            </Button>
                            
                            <div className={`h-12 flex items-center justify-center rounded-md border-2 font-bold px-3 min-w-[4rem] ${
                              isSoldOut
                                ? 'bg-gray-200 border-gray-400 text-gray-500 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-400 text-xl'
                                : isLastCard
                                  ? 'bg-yellow-100 border-yellow-500 text-yellow-700 text-lg'
                                  : isUp && pos > 1
                                    ? 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-500 dark:text-blue-300 text-xl'
                                    : pos > 0 && !isUp
                                      ? pos < product.warning_threshold
                                        ? 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-500 dark:text-red-300 text-xl'
                                        : 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-500 dark:text-blue-300 text-xl'
                                      : 'bg-gray-100 border-gray-300 text-gray-500 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-400 text-xl'
                            }`}>
                              {isSoldOut ? 'Aus' : pos}
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleIncrement(product.id)}
                              disabled={isUp ? isSoldOut : pos >= originalStocks[product.id]}
                              className="h-12 w-12 p-0 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-700 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation select-none"
                            >
                              <Plus className="h-5 w-5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleIncrement5(product.id)}
                              disabled={isUp ? isSoldOut : pos >= originalStocks[product.id] - 4}
                              className="h-10 w-10 p-0 text-gray-500 disabled:opacity-30"
                            >
                              +5
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Verlauf – nur Admin & Stationsleitung, pro Meldung gruppiert (wie Tresor) */}
        {CAN_SEE_HISTORY && (
          <LottoVerlaufGrouped refreshKey={historyRefresh} showSummary={true} />
        )}

        {/* Floating Save Button - immer sichtbar wenn Produkte vorhanden */}
        {products.length > 0 && (
          <div className="fixed bottom-20 md:bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              size="lg"
              className={`h-14 px-6 md:px-8 text-white shadow-2xl rounded-full flex items-center gap-2 md:gap-3 transition-all ${
                hasChanges() 
                  ? 'bg-blue-600 hover:bg-blue-700 scale-105' 
                  : 'bg-gray-500 hover:bg-gray-600'
              }`}
              data-testid="save-report-btn"
            >
              {saving ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Save className="h-5 w-5" />
              )}
              <span className="font-semibold text-sm md:text-base">
                {saving ? 'Speichere...' : hasChanges() ? 'Schicht speichern' : 'Bestand bestätigen'}
              </span>
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
